import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { safeParse, getValidationErrors } from '@/utils/validation';
import {
  type Content,
  type Token,
  RawContentListResponseSchema,
  RawContentResponseSchema,
  ShareResponseSchema,
  type ShareResponse,
  ContentGenerationStatusSchema,
  type ContentGenerationStatus,
  ContentExecutionsResponseSchema,
  type ContentExecutionsResponse,
} from '../types/response';
import type { Session } from '@/auth/useAuth';
import type { Contract } from '../types/contract';

export const useGetContents = (
  session: Session | null | undefined,
  account: string,
  community_id: string,
  revealed?: boolean,
  pagination?: { page: number; size: number },
  order?: { sort_by: 'created_at'; sort_order: 'asc' | 'desc' },
  type?: 'image' | 'video' | 'sticker' | 'animated_sticker',
  preferredFormats: string = 'webm'
) => {
  const paginationParams = pagination || { page: 1, size: 10 };
  const orderParams = order || { sort_by: 'created_at', sort_order: 'desc' };
  return useQuery({
    queryKey: [
      'content',
      session?.id,
      account,
      community_id,
      paginationParams.page,
      paginationParams.size,
      revealed,
      type,
      preferredFormats,
    ],
    queryFn: async ({ signal }) => {
      if (!session) return;

      const queryParams = new URLSearchParams({
        account: account,
        community_id: community_id,
        page: paginationParams?.page.toString(),
        size: paginationParams?.size.toString(),
        sort_by: orderParams?.sort_by,
        sort_order: orderParams?.sort_order,
        ...(revealed != null && { revealed: revealed.toString() }),
        ...(type && { type }),
      });

      // Add preferred_formats parameter
      queryParams.append('preferred_formats', preferredFormats);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session?.authToken,
          },
          signal,
        }
      );
      if (!response.ok) {
        throw Error('Failed to GET contents');
      }
      const data = await response.json();

      // Validate and transform with Zod schema
      const result = safeParse(RawContentListResponseSchema, data);
      console.log('1', result);
      if (!result) {
        const errors = getValidationErrors(RawContentListResponseSchema, data);
        console.error('Content validation errors:', errors);
        console.error('Raw data:', JSON.stringify(data, null, 2));
        throw new Error('Invalid content response format');
      }

      return {
        pagination: result.pagination,
        contents: result.data as Content[],
      };
    },
    enabled: !!session && !!community_id,
    retry: false,
  });
};

export const useGetContentByPrompt = (
  session: Session | null | undefined,
  promptId: number | null,
  pagination?: { page: number; size: number },
  preferredFormats: string = 'webm'
) => {
  const paginationParams = pagination || { page: 1, size: 10 };

  return useQuery({
    queryKey: [
      'content-by-prompt',
      session?.id,
      promptId,
      paginationParams.page,
      paginationParams.size,
      preferredFormats,
    ],
    queryFn: async ({ signal }) => {
      if (!session || !promptId) return;

      const queryParams = new URLSearchParams({
        prompt_id: promptId.toString(),
        page: paginationParams.page.toString(),
        size: paginationParams.size.toString(),
        sort_by: 'created_at',
        sort_order: 'desc',
      });

      // Add preferred_formats parameter
      queryParams.append('preferred_formats', preferredFormats);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session?.authToken,
          },
          signal,
        }
      );
      if (!response.ok) {
        throw Error('Failed to GET content by prompt');
      }
      const data = await response.json();

      // Validate and transform with Zod schema
      const result = safeParse(RawContentListResponseSchema, data);

      if (!result) {
        const errors = getValidationErrors(RawContentListResponseSchema, data);
        console.error('Content by prompt validation errors:', errors);
        console.error('Raw data:', JSON.stringify(data, null, 2));
        throw new Error('Invalid content by prompt response format');
      }

      return {
        pagination: result.pagination,
        content: result.data as Content[],
      };
    },
    enabled: !!session && !!promptId,
    retry: false,
  });
};

export const usePreviewContentMutation = (
  session: Session | null | undefined
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      promptId,
      contentIds,
      tokens,
      overrideExisting = false,
    }: {
      promptId: number;
      contentIds: number[];
      tokens: Token[];
      overrideExisting?: boolean;
    }) => {
      if (!session) return;
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/prompts/${promptId}/preview/async`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
          body: JSON.stringify({
            prompt_id: promptId,
            content_ids: tokens.length === 0 ? contentIds : undefined,
            tokens: tokens.map(token => ({
              contract: {
                chain: token.contract.chain,
                address: token.contract.address,
                name: token.contract.name,
              } as Contract,
              id: token.id,
            })),
            override_existing: overrideExisting,
          }),
        }
      );
      if (!response.ok) {
        let errorMessage = 'Failed to preview content';
        try {
          const errorData = await response.json();
          if (errorData && typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
        } catch {
          // ignore parse errors and fall back to default message
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      return { contentId: data.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preview-content'] });
    },
  });
};

export const useGetAllPreviews = (
  session: Session | null | undefined,
  community_id: string,
  pagination?: { page: number; size: number },
  preferredFormats: string = 'webm'
) => {
  const paginationParams = pagination || { page: 1, size: 10 };

  return useQuery({
    queryKey: [
      'all-previews',
      community_id,
      paginationParams.page,
      paginationParams.size,
      preferredFormats,
    ],
    queryFn: async () => {
      if (!session) return;

      const queryParams = new URLSearchParams({
        community_id: community_id,
        page: paginationParams.page.toString(),
        size: paginationParams.size.toString(),
        sort_by: 'created_at',
        sort_order: 'desc',
      });

      // Add preferred_formats parameter
      queryParams.append('preferred_formats', preferredFormats);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/prompts/previews?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session?.authToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get all previews: ${response.status}`);
      }

      const data = await response.json();
      const result = safeParse(RawContentListResponseSchema, data);
      if (!result) {
        const errors = getValidationErrors(RawContentListResponseSchema, data);
        console.error('All previews validation errors:', errors);
        throw new Error('Invalid all previews response format');
      }

      // Group by promptId for easy lookup
      const byPromptId = new Map<number, typeof result.data>();
      for (const content of result.data) {
        const pid = content.promptId;
        if (pid == null) continue;
        const numPid = typeof pid === 'string' ? parseInt(pid, 10) : pid;
        if (!byPromptId.has(numPid)) {
          byPromptId.set(numPid, []);
        }
        byPromptId.get(numPid)!.push(content);
      }

      return {
        pagination: result.pagination,
        content: result.data,
        byPromptId,
      };
    },
    enabled: !!session && !!community_id,
    refetchInterval: query => {
      const hasProcessingContent = query.state.data?.content?.some(
        item => item.status === 'processing'
      );
      return hasProcessingContent ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });
};

export const useGetPreviewContent = (
  session: Session | null | undefined,
  promptId: number | null,
  pagination?: { page: number; size: number },
  preferredFormats: string = 'webm'
) => {
  const paginationParams = pagination || { page: 1, size: 10 };

  return useQuery({
    queryKey: [
      'preview-content',
      promptId,
      paginationParams.page,
      paginationParams.size,
      preferredFormats,
    ],
    queryFn: async () => {
      if (!session || !promptId) return;

      try {
        // Build query parameters
        const queryParams = new URLSearchParams({
          page: paginationParams.page.toString(),
          size: paginationParams.size.toString(),
          sort_by: 'created_at',
          sort_order: 'desc',
        });

        // Add preferred_formats parameter
        queryParams.append('preferred_formats', preferredFormats);

        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_URL}/prompts/${promptId}/previews?${queryParams.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: session?.authToken,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(
            `Failed to get preview content: ${response.status} ${response.statusText}`,
            errorText
          );
          throw new Error(
            `Failed to get preview content: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        // Validate and transform with Zod schema
        const result = safeParse(RawContentListResponseSchema, data);
        if (!result) {
          const errors = getValidationErrors(
            RawContentListResponseSchema,
            data
          );
          console.error('Preview content validation errors:', errors);
          console.error('Raw response data:', data);
          throw new Error(
            `Invalid preview content response format: ${JSON.stringify(errors)}`
          );
        }

        return {
          pagination: result.pagination,
          content: result.data as Content[],
        };
      } catch (error) {
        // Enhanced error logging
        console.error('Error in useGetPreviewContent queryFn:', {
          error,
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          promptId,
          pagination: paginationParams,
        });

        // Re-throw with more context
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(
          `Unexpected error fetching preview content: ${String(error)}`
        );
      }
    },
    enabled: !!session && !!promptId,
    refetchInterval: query => {
      // Check if any content has "processing" status
      const hasProcessingContent = query.state.data?.content?.some(
        (item: Content) => item.status === 'processing'
      );
      // Poll every 2 seconds if there's processing content, otherwise don't poll
      return hasProcessingContent ? 2000 : false;
    },
    refetchIntervalInBackground: true, // Continue polling even when tab is not active
  });
};

export const useGenerateContentMutation = (
  session: Session | null | undefined
) => {
  return useMutation({
    mutationFn: async ({
      promptId,
      type,
      inputs = [],
      contentIds = [],
      overrideExisting = false,
      notify = [],
    }: {
      promptId: string;
      type: 'image' | 'gif' | 'sticker' | 'animated_sticker';
      inputs: any[];
      contentIds?: string[];
      overrideExisting?: boolean;
      notify?: string[];
    }): Promise<{ execution_id: string }> => {
      if (!session) {
        throw new Error('Session required');
      }

      if (!inputs || inputs.length === 0) {
        throw new Error('At least one token input required');
      }

      const requestBody = {
        prompt_id: promptId,
        type,
        inputs: inputs,
        content_ids: contentIds.length > 0 ? contentIds : undefined,
        override_existing: overrideExisting,
        notify: notify.length > 0 ? notify : undefined,
      };

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      return data as { execution_id: string };
    },
  });
};

// Poll content generation status
export const useContentGenerationStatus = (
  executionId: string | null,
  options?: { enabled?: boolean }
) => {
  const queryClient = useQueryClient();

  return useQuery<ContentGenerationStatus>({
    queryKey: ['content-generation-status', executionId],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/generate/${executionId}/status`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch status: ${response.status}`);
      }

      const data = await response.json();
      const result = safeParse(ContentGenerationStatusSchema, data);
      if (!result) {
        const errors = getValidationErrors(ContentGenerationStatusSchema, data);
        console.error('Status validation errors:', errors);
        throw new Error('Invalid status response');
      }

      // Invalidate content queries when completed
      if (result.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['preview-content'] });
        queryClient.invalidateQueries({ queryKey: ['favorites'] });
      }

      return result;
    },
    enabled: !!executionId && options?.enabled !== false,
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });
};

// Get pending content executions
export const useContentExecutions = (session: Session | null | undefined) => {
  return useQuery<ContentExecutionsResponse>({
    queryKey: ['content-executions', session?.id],
    queryFn: async () => {
      if (!session) {
        throw new Error('Session required');
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/generate/executions`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.status}`);
      }

      const data = await response.json();
      const result = safeParse(ContentExecutionsResponseSchema, data);
      if (!result) {
        const errors = getValidationErrors(
          ContentExecutionsResponseSchema,
          data
        );
        console.error('Executions validation errors:', errors);
        throw new Error('Invalid executions response');
      }

      return result;
    },
    enabled: !!session,
    refetchInterval: query => {
      const hasProcessing = query.state.data?.executions?.some(
        e => e.status === 'pending' || e.status === 'processing'
      );
      return hasProcessing ? 5000 : false;
    },
    refetchIntervalInBackground: true,
  });
};

export const useRevealContent = (session: Session | null | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contentId: string) => {
      if (!session) {
        throw new Error('Session required');
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/${contentId}/reveal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['my-recent-generations'] });
    },
  });
};

export const useRevealAllContent = (
  session: Session | null | undefined,
  community_id: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!session) {
        throw new Error('Session required');
      }

      // Reveal all content in parallel
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/reveal-all`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
          body: JSON.stringify({ community_id }),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to reveal_all content: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['my-recent-generations'] });
    },
  });
};

export const useUploadContent = (session: Session | null | undefined) => {
  return useMutation({
    mutationFn: async (content: string) => {
      if (!session) {
        throw new Error('Session required');
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
          body: JSON.stringify({ content }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to upload content: ${response.status}`
        );
      }

      const data = await response.json();
      console.log('Content uploaded successfully:', data);
      return data as { id: string; type: string; url: string };
    },
  });
};

// Track content status with polling using /content/{id}
export const useContentExecution = (
  executionId: string,
  session: Session | null | undefined,
  options?: {
    enabled?: boolean;
    preferredFormats?: string;
  }
) => {
  const preferredFormats = options?.preferredFormats || 'webm';

  return useQuery<Content>({
    queryKey: ['content-execution', executionId, preferredFormats],
    queryFn: async () => {
      if (!session) {
        throw new Error('Session required');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('preferred_formats', preferredFormats);

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_URL}/content/${executionId}?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: session.authToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch content status: ${response.status}`);
      }

      const data = await response.json();

      // Validate and transform with Zod schema
      const result = safeParse(RawContentResponseSchema, data);
      if (!result) {
        const errors = getValidationErrors(RawContentResponseSchema, data);
        console.error('Content execution validation errors:', errors);
        throw new Error('Invalid content execution response format');
      }

      return result as Content;
    },
    enabled: !!session && !!executionId && options?.enabled !== false,
    refetchInterval: query => {
      // Poll every 2 seconds if status is processing
      const isProcessing = query.state.data?.status === 'processing';
      return isProcessing ? 2000 : false;
    },
    refetchIntervalInBackground: true, // Continue polling when tab is not active
  });
};

// Share content to external integrations (e.g., Giphy)
export const useShareContent = (session: Session | null | undefined) => {
  return useMutation({
    mutationFn: async (contentId: string) => {
      if (!session) {
        throw new Error('Session required');
      }

      // Create AbortController for 90-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);

      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_URL}/content/${contentId}/share`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: session.authToken,
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Failed to share content: ${response.status}`
          );
        }

        const data = await response.json();

        // Validate and transform with Zod schema
        const result = safeParse(ShareResponseSchema, data);
        if (!result) {
          const errors = getValidationErrors(ShareResponseSchema, data);
          console.error('Share response validation errors:', errors);
          throw new Error('Invalid share response format');
        }

        return result as ShareResponse;
      } catch (error) {
        clearTimeout(timeoutId);

        // Distinguish timeout errors from other failures
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error(
            'Request timed out. Giphy upload is taking longer than expected.'
          );
        }

        throw error;
      }
    },
    retry: 10, // Retry up to 10 times on failure
    retryDelay: 1000, // Fixed 1 second delay between retries
  });
};
